
import Vue from 'vue'
import JcdcCheckboxSimple from '@/components/jcdc/JcdcCheckboxSimple.vue'
import JcdcDataTableGroupIcon from '@/components/jcdc/JcdcDataTableGroupIcon.vue'
import JcdcBooleanIcons from './JcdcBooleanIcons.vue'
import { VChip, VDataTable, VSkeletonLoader } from 'vuetify/lib'
import { heatMapColors } from '@/plugins/theme'

import {
  formatCurrency,
  formatDateValue,
  formatDisplayDate,
  formatDisplayDateTime,
  formatDisplayTime,
  formatFileSize,
  formatNumber,
  formatPercent,
  formatPhone,
} from '@/util/formatters'

import {
  AppendItem,
  JcdcTableHeader,
  PrependItem,
  condenseHeaders,
  items,
  noDataText,
} from '@/util/tableUtilities'

export default Vue.extend({
  name: 'JcdcDataTable',
  components: {
    VDataTable,
    VSkeletonLoader,
    VChip,
    JcdcDataTableGroupIcon,
    JcdcBooleanIcons,
    JcdcCheckboxSimple,
  },
  props: {
    loading: { type: Boolean, default: false },
    prependHeader: {
      type: Array as () => PrependItem[][],
      default: (): [] => [],
    },
    prependHeaderMobile: {
      type: Array as () => JcdcTableHeader[],
      default: (): [] => [],
    },
    headers: { type: Array as () => JcdcTableHeader[], required: true },
    items: { type: Array as () => items[], default: (): [] => [] },
    defaultValue: { type: Array as () => items[], default: (): [] => [] },
    totalRow: { type: Array as () => AppendItem[], default: (): [] => [] },
    // custom props
    showAll: { type: Boolean, default: false },
    simple: { type: Boolean, default: false },
    selectVisible: { type: Boolean, default: false },
    selectableKey: { type: String, default: 'isSelectable' },
    showCount: { type: Boolean, default: false },
    // overwritten props should still be accessible
    disableSort: { type: Boolean, default: false },
    disableFiltering: { type: Boolean, default: false },
    disablePagination: { type: Boolean, default: false },
    hideDefaultFooter: { type: Boolean, default: false },
    singleSelect: { type: Boolean, default: false },
    showExpand: { type: Boolean, default: false },
    customExpand: { type: Boolean, default: false },
    // No Data
    noDataText: { type: String, default: noDataText },
  },
  data() {
    return {
      headersMobile: this.prependHeaderMobile,
      headersExpand: [] as JcdcTableHeader[],
      selected: this.defaultValue,
    }
  },
  computed: {
    responsiveHeaders(): JcdcTableHeader[] {
      if (this.prependHeader.length > 0 && this.$vuetify.breakpoint.xsOnly) {
        return this.headersMobile
      }
      return this.headersExpand
    },
  },
  beforeMount() {
    this.headersExpand = this.headers.map((x) => {
      return {
        ...x,
        ...this.headerFormatter(x.formatter),
        divider: true,
      }
    })
    if (this.showExpand && !this.customExpand) {
      this.headersExpand = [
        {
          value: 'data-table-expand',
          divider: true,
          text: 'Expand',
          class: 'hide-text',
        },
        ...this.headersExpand,
      ]
    }
    if (this.prependHeader && this.prependHeaderMobile.length == 0) {
      this.headersMobile = this.condenseHeaders(
        this.headersExpand,
        this.prependHeader
      )
      this.$emit('headers-mobile', this.headersMobile)
    }
  },
  methods: {
    condenseHeaders,
    // FORMATTER
    numberFormatter(value, formatter, digits): string {
      switch (formatter) {
        case 'percent':
          return formatPercent(value, digits)
        case 'currency':
          return formatCurrency(value, digits)
        case 'phone':
          return formatPhone(value)
        case 'date':
          return formatDisplayDate(value)
        case 'dateValue':
          return formatDateValue(value)
        case 'dateTime':
          return formatDisplayDateTime(value)
        case 'time':
          return formatDisplayTime(value)
        case 'fileSize':
          return formatFileSize(value)
        case 'average':
          return formatNumber(value, 2)
        default:
          return formatNumber(value, digits)
      }
    },
    headerFormatter(formatter): object {
      switch (formatter) {
        case 'percent':
          return { align: 'end' }
        case 'currency':
          return { align: 'end' }
        case 'fileSize':
          return { align: 'end' }
        case 'number':
          return { align: 'end' }
        case 'average':
          return { align: 'end' }
        case 'boolean':
          return { align: 'center' }
        default:
          return { align: 'start' }
      }
    },
    // Select All
    toggleSelectAll(event) {
      if (event) {
        this.selected = this.items.filter((item) => {
          return !Object.prototype.hasOwnProperty.call(item, this.selectableKey)
            ? true
            : item[this.selectableKey] != false
        })
      } else {
        this.selected = []
      }
    },
    // Heat Map
    chipColor(item, column, items, color) {
      const stops = Object.keys(heatMapColors[color]).length,
        array = items.map((x) => x[column]).filter((x) => typeof x == 'number'),
        max = Math.max(...array),
        min = Math.min(...array),
        range = max - min,
        rangeGrp = range / stops
      for (let i = 1; i < stops; i++) {
        if (item < min + rangeGrp * i) return `lighten-${stops - i}`
      }
      return ''
    },
  },
})
